import { useState, useEffect, useRef, useCallback } from "react";
import { useGetAllStudentsQuery } from "../../../peoples/students/api/studentApi";
import { StudentResponseType } from "../../../peoples/students/types/studentResponse";
import {
  StudentAttendanceTypes,
  AttendanceApiResponse,
} from "../types/attendanceTypes";
import { API_CONFIG } from "../../../../core/config";
import {
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useCreateBulkAttendanceMutation,
  useUpdateBulkAttendanceMutation,
} from "../api/attendanceApi";
import { showToast } from "../../../../core/utils/alert";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import dayjs from "dayjs";

const isValidDbId = (id: any): id is number =>
  typeof id === "number" && Number.isInteger(id) && id > 0 && id < 1000000000;

export const useAttendance = () => {
  const LAZY_PAGE_SIZE = 100;
  const currentDate = new Date().toISOString().split("T")[0];
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>([]);
  const [bulkStatus, setBulkStatus] = useState("Present");

  const getDefaultDate = () => dayjs().format("YYYY-MM-DD");
  const [filterDate, setFilterDate] = useState(getDefaultDate());

  const [students, setStudents] = useState<StudentAttendanceTypes[]>([]);

  // Local attendance overrides — when the user marks/changes attendance we update
  // this map immediately so the UI is responsive without waiting for a full refetch.
  // Shape: { [student_id]: AttendanceApiResponse }
  const [localAttendanceOverrides, setLocalAttendanceOverrides] = useState<
    Record<number, AttendanceApiResponse>
  >({});

  const [filters, setFilters] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem("studentAttendanceFilters");
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      return Object.fromEntries(
        Object.entries(parsed).filter(([k, v]) => {
          if (v === undefined || v === null || v === "") return false;
          if (k === "status" || k === "attendance_status") return typeof v === "string" && v.length > 0;
          const n = Number(v);
          return !isNaN(n) && n > 0;
        })
      );
    } catch {
      return {};
    }
  });

  const [tempFilters, setTempFilters] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem("studentAttendanceFilters");
      if (!saved) return {};
      const parsed = JSON.parse(saved);
      return Object.fromEntries(
        Object.entries(parsed).filter(([k, v]) => {
          if (v === undefined || v === null || v === "") return false;
          if (k === "status" || k === "attendance_status") return typeof v === "string" && v.length > 0;
          const n = Number(v);
          return !isNaN(n) && n > 0;
        })
      );
    } catch {
      return {};
    }
  });

  const [selectedCourseStages, setSelectedCourseStages] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<any>({});

  useEffect(() => {
    sessionStorage.setItem("studentAttendanceFilters", JSON.stringify(filters));
  }, [filters]);

  // Pagination & Lazy Loading States
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState("");
  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<StudentAttendanceTypes[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  // ─── SINGLE API CALL ────────────────────────────────────────────────────────
  // Pass `date` so the backend embeds each student's attendance record for that
  // date directly in the response — no separate attendance API call needed.
  const { data: studentData, isLoading, isFetching } = useGetAllStudentsQuery({
    page: isAllMode ? lazyPage : pageNumber,
    size: isAllMode ? LAZY_PAGE_SIZE : recordsPerPage,
    search: search,
    status: "active",
    date: filterDate,
    ...filters,
    ...sortConfig,
  });
  // ────────────────────────────────────────────────────────────────────────────

  // Clear local overrides when date/filters/search changes (new backend data)
  useEffect(() => {
    setLocalAttendanceOverrides({});
  }, [filterDate, filters, sortConfig, search]);

  useEffect(() => {
    setAccumulatedStudents([]);
    setLazyPage(1);
    setLazyHasMore(true);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
  }, [search, filters, sortConfig, filterDate]);

  const { batchTimings } = useBatchTiming();

  // Resolve attendance for a student: prefer local override, then embedded backend value
  const resolveAttendance = useCallback(
    (student: StudentResponseType): { id?: number; attendance_status: string } | null => {
      const override = localAttendanceOverrides[student.id];
      if (override) return override;
      if (student.attendance) {
        return {
          id: student.attendance.id,
          attendance_status: student.attendance.attendance_status,
        };
      }
      return null;
    },
    [localAttendanceOverrides]
  );

  useEffect(() => {
    if (studentData?.data) {
      const studentItems = studentData.data.items || [];

      const updatedStudents: StudentAttendanceTypes[] = studentItems
        .filter((student) => student.status !== "pending")
        .map((student: StudentResponseType) => {
          const resolved = resolveAttendance(student);

          const photoUrl = student.docs.photo
            ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo}?v=${Date.now()}`
            : student.profile.gender?.toLowerCase() === "female"
              ? "assets/img/profiles/avatar-03.jpg"
              : "assets/img/profiles/avatar-02.jpg";

          const attendanceStatus = resolved
            ? resolved.attendance_status.charAt(0).toUpperCase() +
              resolved.attendance_status.slice(1).toLowerCase()
            : "";

          const levelName = student.course?.stage?.name || "-";

          return {
            id: resolved?.id || student.id,
            studentId: student.profile.studentid || "",
            name: student.name || "",
            course: student.course?.name || "",
            level: levelName,
            attendanceDate: filterDate,
            attendanceStatus,
            week: student.current_week,
            registrationDate: student.registration_date ? student.registration_date.split("T")[0] : undefined,
            student: {
              id: student.id,
              name: student.name,
              status: student.status,
              registration_date: student.registration_date,
              center: student.center
                ? {
                    id: student.center.id,
                    name: student.center.name,
                    code: student.center.code,
                    zone: student.center.zone,
                    district: student.center.district,
                    status: student.center.status,
                  }
                : undefined,
            },
            img: photoUrl,
            gender: student.profile.gender || "male",
            key: student.id,
          };
        });

      if (isAllMode) {
        if (isFetching) return;
        if (lazyPage === lastProcessedPage.current) return;

        const isLastPage = updatedStudents.length < LAZY_PAGE_SIZE;
        setLazyHasMore(!isLastPage);
        lastProcessedPage.current = lazyPage;
        pageRequestedRef.current = false;

        setAccumulatedStudents(prev => {
          if (lazyPage === 1) return updatedStudents;
          const newUnique = updatedStudents.filter(
            newS => !prev.some(p => p.key === newS.key)
          );
          return [...prev, ...newUnique];
        });

        if (!isLastPage && !pageRequestedRef.current) {
          pageRequestedRef.current = true;
          setLazyPage(prev => prev + 1);
        }
      } else {
        setStudents(updatedStudents);
      }
    }
  }, [studentData, filterDate, batchTimings, isAllMode, lazyPage, isFetching, resolveAttendance]);

  const loadNextLazyPage = useCallback(() => {
    if (!isAllMode || !lazyHasMore) return;
    if (pageRequestedRef.current) return;
    pageRequestedRef.current = true;
    setLazyPage(prev => prev + 1);
  }, [isAllMode, lazyHasMore]);

  const resetLazyState = useCallback((initialStudents: any[] = []) => {
    setAccumulatedStudents(initialStudents);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    if (pageSize !== undefined) {
      const total = studentData?.data?.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState(students || []);
        setRecordsPerPage(pageSize);
        return;
      }

      if (isAllMode) {
        setIsAllMode(false);
        resetLazyState();
      }
      setRecordsPerPage(pageSize);
    }
    setPageNumber(page);
  };

  const [createAttendance] = useCreateAttendanceMutation();
  const [updateAttendance] = useUpdateAttendanceMutation();
  const [createBulkAttendance, { isLoading: isCreatingBulk }] = useCreateBulkAttendanceMutation();
  const [updateBulkAttendance, { isLoading: isUpdatingBulk }] = useUpdateBulkAttendanceMutation();

  const isBulkUpdating = isCreatingBulk || isUpdatingBulk;

  // Get local override for a student (set after user marks attendance in this session)
  const getLocalAttendance = (studentKey: React.Key) =>
    localAttendanceOverrides[Number(studentKey)];

  // Get the embedded attendance from the backend response for a student
  const getEmbeddedAttendance = useCallback((studentKey: React.Key) => {
    const studentItems = studentData?.data?.items || [];
    const student = studentItems.find((s) => s.id === Number(studentKey));
    return student?.attendance ?? null;
  }, [studentData]);

  const handleOptionChange = useCallback(async (
    studentKey: React.Key,
    newStatus: string
  ) => {
    try {
      const localOverride = getLocalAttendance(studentKey);
      const embedded = getEmbeddedAttendance(studentKey);
      const existingRecord = localOverride ?? embedded;
      const existingId =
        (localOverride && isValidDbId(localOverride.id) ? localOverride.id : null) ??
        (embedded && isValidDbId(embedded.id) ? embedded.id : null);

      const targetStudent = (isAllMode ? accumulatedStudents : students).find(s => s.key === studentKey);
      const studentRegDate = targetStudent?.registrationDate ? targetStudent.registrationDate.split("T")[0] : null;
      if (studentRegDate && filterDate < studentRegDate) {
        showToast({
          title: "Error",
          text: `Cannot mark attendance before student's date of join (${dayjs(studentRegDate).format("DD-MM-YYYY")}).`,
          icon: "error",
          timer: 5000,
        });
        return;
      }

      if (filterDate < currentDate && existingRecord?.attendance_status) {
        showToast({
          title: "Error",
          text: "Cannot update attendance for past dates.",
          icon: "error",
          timer: 5000,
        });
        return;
      }

      if (existingId) {
        await updateAttendance({
          id: existingId,
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        setLocalAttendanceOverrides((prev) => ({
          ...prev,
          [Number(studentKey)]: {
            id: existingId,
            student_id: Number(studentKey),
            attendance_status: newStatus.toLowerCase(),
            date: filterDate,
          } as AttendanceApiResponse,
        }));

        showToast({ title: "Success", text: "Attendance updated successfully", icon: "success", timer: 5000 });
      } else {
        const response = await createAttendance({
          student_id: Number(studentKey),
          attendance_status: newStatus.toLowerCase(),
          date: filterDate,
        }).unwrap();

        setLocalAttendanceOverrides((prev) => ({
          ...prev,
          [Number(studentKey)]: {
            id: isValidDbId((response.data as any)?.attendance?.id)
              ? Number((response.data as any).attendance.id)
              : isValidDbId((response.data as any)?.id)
              ? Number((response.data as any).id)
              : undefined,
            student_id: Number(studentKey),
            attendance_status: newStatus.toLowerCase(),
            date: filterDate,
          } as AttendanceApiResponse,
        }));

        showToast({ title: "Success", text: "Attendance marked successfully", icon: "success", timer: 5000 });
      }

      // Sync local student list state for immediate UI update
      const patchStatus = (prev: StudentAttendanceTypes[]) =>
        prev.map((s) =>
          s.key === studentKey
            ? { ...s, attendanceStatus: newStatus.charAt(0).toUpperCase() + newStatus.slice(1).toLowerCase() }
            : s
        );

      setStudents(patchStatus);
      setAccumulatedStudents(patchStatus);
    } catch (error) {
      console.error("Failed to update attendance:", error);
      showToast({ title: "Error", text: "Failed to update attendance. Please try again.", icon: "error", timer: 5000 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, localAttendanceOverrides, filterDate, currentDate, updateAttendance, createAttendance, getEmbeddedAttendance]);

  const handleBulkStatusChange = async () => {
    if (selectedStudentKeys.length === 0 || !bulkStatus) return;

    try {
      const currentStudents = isAllMode ? accumulatedStudents : students;
      const selectedStudents = currentStudents.filter((s) =>
        selectedStudentKeys.includes(s.key)
      );

      const hasPastDateAttendance = selectedStudents.some((student) => {
        const existing = getLocalAttendance(student.key) ?? getEmbeddedAttendance(student.key);
        return filterDate < currentDate && existing?.attendance_status;
      });

      const beforeJoinStudents = selectedStudents.filter((student) => {
        const regDate = student.registrationDate ? student.registrationDate.split("T")[0] : null;
        return regDate && filterDate < regDate;
      });

      if (beforeJoinStudents.length > 0) {
        showToast({
          title: "Error",
          text: `Cannot mark attendance for ${beforeJoinStudents.length} student(s) before their date of join.`,
          icon: "error",
          timer: 5000,
        });
        return;
      }

      if (hasPastDateAttendance) {
        showToast({ title: "Error", text: "Cannot update attendance for past dates.", icon: "error", timer: 5000 });
        return;
      }

      const studentsToUpdate: Array<{ id: number; student_id: number; attendance_status: string; date: string }> = [];
      const studentsToCreate: typeof selectedStudents = [];

      selectedStudents.forEach((student) => {
        const local = getLocalAttendance(student.key);
        const embedded = getEmbeddedAttendance(student.key);
        const candidateId =
          (local && isValidDbId(local.id) ? local.id : null) ??
          (embedded && isValidDbId(embedded.id) ? embedded.id : null);

        if (candidateId) {
          studentsToUpdate.push({
            id: candidateId,
            student_id: Number(student.key),
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          });
        } else {
          studentsToCreate.push(student);
        }
      });

      if (studentsToUpdate.length > 0) {
        await updateBulkAttendance({ attendance: studentsToUpdate }).unwrap();
        studentsToUpdate.forEach((u) => {
          setLocalAttendanceOverrides((prev) => ({
            ...prev,
            [u.student_id]: { id: u.id, student_id: u.student_id, attendance_status: u.attendance_status, date: u.date } as AttendanceApiResponse,
          }));
        });
      }

      if (studentsToCreate.length > 0) {
        const response = await createBulkAttendance({
          attendance: studentsToCreate.map((s) => ({
            student_id: Number(s.key),
            attendance_status: bulkStatus.toLowerCase(),
            date: filterDate,
          })),
        }).unwrap();

        const createdItems: any[] = Array.isArray(response.data)
          ? response.data
          : (response.data as any)?.items ?? (response.data as any)?.attendance ?? [];

        const createdMap = new Map<number, number>();
        createdItems.forEach((item: any) => {
          if (item?.student_id && isValidDbId(item.id)) {
            createdMap.set(Number(item.student_id), Number(item.id));
          }
        });

        studentsToCreate.forEach((student) => {
          const studentIdNum = Number(student.key);
          const realId = createdMap.get(studentIdNum);
          setLocalAttendanceOverrides((prev) => ({
            ...prev,
            [studentIdNum]: {
              id: realId,
              student_id: studentIdNum,
              attendance_status: bulkStatus.toLowerCase(),
              date: filterDate,
            } as AttendanceApiResponse,
          }));
        });
      }

      showToast({
        title: "Success",
        text: `Attendance ${
          studentsToUpdate.length > 0 && studentsToCreate.length > 0
            ? "updated and marked"
            : studentsToUpdate.length > 0
            ? "updated"
            : "marked"
        } for ${selectedStudentKeys.length} students`,
        icon: "success",
        timer: 5000,
      });

      const selectedKeysSet = new Set(selectedStudentKeys);
      const patchBulk = (prev: StudentAttendanceTypes[]) =>
        prev.map((s) =>
          selectedKeysSet.has(s.key)
            ? { ...s, attendanceStatus: bulkStatus.charAt(0).toUpperCase() + bulkStatus.slice(1).toLowerCase() }
            : s
        );

      setStudents(patchBulk);
      setAccumulatedStudents(patchBulk);
      setSelectedStudentKeys([]);
    } catch (error) {
      console.error("Failed to update bulk attendance:", error);
      showToast({ title: "Error", text: "Failed to update bulk attendance. Please try again.", icon: "error", timer: 5000 });
    }
  };

  const getStatusColor = useCallback((status: string) => {
    switch (status.toLowerCase()) {
      case "present":  return "bg-success-transparent text-success";
      case "ncns":     return "bg-warning-transparent text-warning";
      case "dropout":  return "bg-info-transparent text-info";
      case "leave":    return "bg-dark-transparent text-dark";
      case "absent":   return "bg-danger-transparent text-danger";
      default:         return "";
    }
  }, []);

  const getInitialAttendanceStatus = (status: string) => (status ? status.toLowerCase() : "");

  return {
    students,
    isLoading,
    selectedStudentKeys,
    bulkStatus,
    filterDate,
    currentDate,
    paginationData: {
      totalPages: studentData?.data?.totalPages || 1,
      pageSize: studentData?.data?.pageSize || 10,
      totalItems: studentData?.data?.totalItems || 0,
      currentPage: studentData?.data?.currentPage || 1,
    },
    setSelectedStudentKeys,
    setBulkStatus,
    setFilterDate,
    handleOptionChange,
    handleBulkStatusChange,
    isBulkUpdating,
    getStatusColor,
    getInitialAttendanceStatus,
    filters,
    tempFilters,
    setTempFilters,
    handleFilterChange: (filterName: string, value: any, courses?: any[]) => {
      const parsedValue =
        filterName === "status" || filterName === "attendance_status"
          ? value?.value || undefined
          : value?.value && !isNaN(Number(value.value))
          ? Number(value.value)
          : value?.value || undefined;

      setTempFilters((prev: any) => ({ ...prev, [filterName]: parsedValue }));

      if (filterName === "course_id" && courses) {
        const selectedCourse = courses.find((c: any) => c.id.toString() === value?.value);
        setSelectedCourseStages(selectedCourse?.courseStages || []);
        setTempFilters((prev: any) => ({ ...prev, stage_id: undefined }));
      }
    },
    handleApplyFilters: () => {
      setFilters(tempFilters);
      setPageNumber(1);
      if (isAllMode) {
        resetLazyState();
      }
    },
    handleResetFilters: () => {
      const emptyFilters = {
        center_id: undefined,
        course_id: undefined,
        stage_id: undefined,
        status: undefined,
        attendance_status: undefined,
        session_id: undefined,
      };
      setFilters(emptyFilters);
      setTempFilters(emptyFilters);
      setSelectedCourseStages([]);
      setSortConfig({});
      setPageNumber(1);
      if (isAllMode) {
        resetLazyState();
      }
    },
    selectedCourseStages,
    sortConfig,
    handleSortChange: (sortBy: string, order: "asc" | "desc") => {
      const newOrder =
        sortConfig.sort_by === sortBy && sortConfig.sort_order === "asc" ? "desc" : "asc";
      setSortConfig({ sort_by: sortBy, sort_order: newOrder });
    },
    pageNumber,
    recordsPerPage,
    search,
    handlePageChange,
    handleSearchChange,
    isAllMode,
    setIsAllMode,
    accumulatedStudents,
    lazyHasMore,
    isLazyLoading: isFetching && pageRequestedRef.current,
    loadNextLazyPage,
  };
};
